"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./FancyLoadingMaskGLRenderer");
const _ = require("lodash");

class FancyLoadingMaskGLComponent extends EventEmitter {
    /**
     * @member shown {Boolean} - Get the 'shown' state of the veil.
     * @member backgroundHidden {Boolean} - Get/set the visual state of the background color.
     * @member renderer {FancyLoadingMaskGLRenderer} - Get a reference to the associated renderer instance.
     * @member darkTheme {Boolean} - Get/set the the 'dark theme' of the veil.
     */

    /**
     * Create a model for the veil.
     * @param [configOptions.shown] {Boolean} - Shown. If true, the loading mask is shown immediately (false by
     * default).
     * @param [configOptions.backgroundHidden] {Boolean} - Background Hidden. If true, the background of the
     * loading mask is hidden (false by default).
     * @param [configOptions.darkTheme] {Boolean} - Dark Theme. If true, the dark theme is applied to the loading mask
     * @param [configOptions.message] {String} - Message to show below the loader, optional parameter
     * (false by default).
     */
    constructor(configOptions) {
        super();
        this._ANIMATION_DURATION = 333;
        this._ANIMATION_DEBOUNCE = 250;
        this._doNotShow = false;
        this._showAcknowledged = false;
        this._hideAcknowledged = false;
        this._message = _.get(configOptions, "message", false);
        this._shown = _.get(configOptions, "shown", false);
        this._backgroundHidden = _.get(configOptions, "backgroundHidden", false);
        this._darkTheme = _.get(configOptions, "darkTheme", false);
        this._renderer = new Renderer(this);
        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/

    /**
     * Returns the renderer associated with this control
     * @returns {FancyLoadingMaskGLRenderer}
     */
    getRenderer() {
        return this._renderer;
    }
    get renderer() {
        return this.getRenderer();
    }

    /**
     * The public shutdown function that returns a promise that is resolved when
     * the renderer has completed its shutdown routine.
     * @returns {Promise}
     */
    shutdown() {
        this._unregisterListeners();
        return this._renderer.shutdown().then(() => {
            this._shown = undefined;
            this._backgroundHidden = undefined;
            this._darkTheme = undefined;
            this._message = undefined;
            this._ANIMATION_DURATION = undefined;
            this._ANIMATION_DEBOUNCE = undefined;
            this._doNotShow = undefined;
            this._showAcknowledged = undefined;
            this._hideAcknowledged = undefined;
            this._renderer = undefined;
        });
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    /**
     * Returns the shown state.
     * @returns {Boolean}
     */
    getShown() {
        return this._shown;
    }
    /**
     * Shown state check.
     * @returns {Boolean}
     */
    isShown() {
        return this.getShown();
    }
    get shown() {
        return this.getShown();
    }

    /**
     * Toggles the visible state of the background.  By default, the background is shown.
     * @param newState - Boolean
     * @returns {FancyLoadingMaskGLComponent}
     */
    setBackgroundHidden(newState) {
        this._backgroundHidden = newState;
        this.emit("UpdateHiddenBackgroundState", this._backgroundHidden);
        return this;
    }
    /**
     * Returns the hidden state of the background state.
     * @returns {Boolean}
     */
    getBackgroundHidden() {
        return this._backgroundHidden;
    }
    /**
     * Hidden background state check.
     * @returns {Boolean}
     */
    isBackgroundHidden() {
        return this.getBackgroundHidden();
    }
    set backgroundHidden(newState) {
        this.setBackgroundHidden(newState);
    }
    get backgroundHidden() {
        return this.getBackgroundHidden();
    }

    /**
     * Toggles the dark/light theme
     * @param newState - Boolean
     * @returns {FancyLoadingMaskGLComponent}
     */
    setDarkTheme(newState) {
        this._darkTheme = newState;
        this.emit("UpdateDarkTheme", this._darkTheme);
        return this;
    }
    /**
     * Returns the state of the dark theme.
     * @returns {Boolean}
     */
    getDarkTheme() {
        return this._darkTheme;
    }
    /**
     * Dark theme state check.
     * @returns {Boolean}
     */
    isDarkTheme() {
        return this.getDarkTheme();
    }
    set darkTheme(newState) {
        this.setDarkTheme(newState);
    }
    get darkTheme() {
        return this.getDarkTheme();
    }

    /**
     * Shows the loading mask and resolves a promise once shown.
     * @returns {Promise}
     */
    show() {
        if (!this._showAcknowledged && !this._shown) {
            this._showAcknowledged = true;
            return new Promise((resolve) => {
                setTimeout(() => {
                    this._showAcknowledged = false;
                    if (this._doNotShow) {
                        // this._showAcknowledged = false;
                        resolve();
                    } else {
                        setTimeout(() => {
                            // this._showAcknowledged = false;
                            resolve();
                        }, this._ANIMATION_DURATION);
                        this._shown = true;
                        this.emit("Show");
                    }
                }, this._ANIMATION_DEBOUNCE);
            });
        } else {
            return Promise.reject("Show called multiple times before a hide");
        }
    }

    /**
     * Hides the loading mask and resolves a promise once hidden.
     * @returns {Promise}
     */
    hide() {
        if (!this._hideAcknowledged) {
            if (!this._showAcknowledged) {
                if (this._shown) {
                    this._hideAcknowledged = true;
                    return new Promise((resolve) => {
                        setTimeout(() => {
                            this._hideAcknowledged = false;
                            this._shown = false;
                            this._doNotShow = false;
                            resolve();
                        }, this._ANIMATION_DURATION);
                        this.emit("Hide");
                        this._doNotShow = true;
                    });
                } else {
                    return Promise.reject("Hide called multiple times after a show");
                }
            } else {
                return new Promise((resolve) => {
                    this._hideAcknowledged = false;
                    this._doNotShow = true;
                    setTimeout(() => {
                        this._doNotShow = false;
                    }, this._ANIMATION_DEBOUNCE);
                    resolve();
                });
            }
        } else {
            return Promise.reject("Hide called multiple times after a show");
        }
    }

    showProgress(progressMsg) {
        this.emit("Progress", progressMsg);
    }

    /**
     * Shows the background.
     */
    showBackground() {
        this.setBackgroundHidden(false);
    }

    /**
     * Hides the background.
     */
    hideBackground() {
        this.setBackgroundHidden(true);
    }

    /**
     * Applies the dark theme.
     */
    showDarkTheme() {
        this.setDarkTheme(true);
    }

    /**
     * Applies the light theme by removing the dark theme.
     */
    showLightTheme() {
        this.setDarkTheme(false);
    }

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}

module.exports = FancyLoadingMaskGLComponent;
